import type { SalesHealthReason, SalesScoreCatalog } from '../../api/types';
import { contributionShare, healthPercent } from './salesViewFormat';
import { buildBreakdownRows, rawSeverity, undampedContribution } from './salesBreakdownRows';
import { METRIC_TIPS } from './salesMetricTips';

/** Denominator used only until the catalogue loads. `catalog.total_weight` is
 *  authoritative -- it is derived from the live ScoreConfig. */
const FALLBACK_TOTAL_WEIGHT = 27;

interface SalesScoreBreakdownProps {
  healthScore: number;
  reasons: SalesHealthReason[];
  scoreVersion?: string | null;
  /** The full signal set. Absent until it loads, in which case the table shows
   *  the firing rows only rather than blocking on it. */
  catalog?: SalesScoreCatalog | null;
}

/**
 * The arithmetic behind the score, itemised (§4.4 / §6.5).
 *
 * This reconciles exactly. Every firing signal is on the payload, so the
 * contributions sum to the risk score and the risk subtracts to the health
 * percent shown on the card. It did NOT reconcile until `max_reasons` was
 * raised from 5 to 13 -- the truncated payload hid an average 0.063 of risk on
 * URGENT clients, which is precisely where someone asks how the number was made.
 *
 * Each row is `contribution = weight x severity`, where severity is 0-1 for how
 * badly that one signal fired. A signal at full severity contributes its whole
 * weight; a partial signal contributes a fraction.
 */
export function SalesScoreBreakdown({
  healthScore,
  reasons,
  scoreVersion,
  catalog,
}: SalesScoreBreakdownProps) {
  const rows = buildBreakdownRows(reasons, catalog);
  const firingCount = rows.filter((r) => r.firing).length;
  const totalWeight = catalog?.total_weight ?? FALLBACK_TOTAL_WEIGHT;
  const riskPoints = rows.reduce((sum, r) => sum + r.contribution, 0);
  const riskPct = Math.round(contributionShare(riskPoints, totalWeight) * 100);
  const health = healthPercent(healthScore) ?? 0;
  const signalCount = catalog?.signals.length ?? 13;

  return (
    <div className="sv-breakdown">
      {firingCount === 0 && (
        <div className="sv-breakdown-total">
          Health <strong>{health}%</strong> — no risk signals firing.
        </div>
      )}

      <table className="sv-breakdown-table">
        <thead>
          <tr>
            <th>Signal</th>
            <th className="sv-breakdown-th-window">Measured over</th>
            <th className="sv-num">Weight</th>
            <th className="sv-num">Severity</th>
            <th className="sv-num">Risk pts</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const severity = rawSeverity(r);
            return (
            <tr key={r.key} className={r.firing ? '' : 'sv-breakdown-dormant'}>
              <td>
                <div className="sv-breakdown-label" title={r.description}>{r.name}</div>
                <div className="sv-breakdown-group">{r.group}</div>
                {r.guards?.length ? (
                  <div
                    className="sv-breakdown-guards"
                    title={r.guards.some((g) => g.label.startsWith('market'))
                      ? METRIC_TIPS.marketDefinition : undefined}
                  >
                    Guards: {r.guards.map((g) => `${g.label} ×${g.factor}`).join(' · ')}
                  </div>
                ) : null}
              </td>
              <td className="sv-breakdown-window">{r.windows ?? '—'}</td>
              <td className="sv-num">{r.weight.toFixed(1)}</td>
              <td className="sv-num" title={r.severityBasis}>
                {severity === null ? '—' : `${Math.round(severity * 100)}%`}
                {r.firing && r.severityBasis ? (
                  <div className="sv-breakdown-basis">{r.severityBasis}</div>
                ) : null}
              </td>
              <td className="sv-num">
                {r.guards?.length ? (
                  <span className="sv-breakdown-before">
                    {(undampedContribution(r) ?? 0).toFixed(2)}{' → '}
                  </span>
                ) : null}
                {r.contribution.toFixed(2)}
              </td>
            </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>
              Total risk
              <div className="sv-breakdown-group">
                {firingCount} of {signalCount} signals firing
              </div>
            </td>
            <td />
            <td className="sv-num">{totalWeight}</td>
            <td className="sv-num" />
            <td className="sv-num">{riskPoints.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="sv-breakdown-total">
        {riskPoints.toFixed(2)} ÷ {totalWeight} = <strong>{riskPct}% risk</strong>
        {' → '}
        100% − {riskPct}% = <strong>{health}% health</strong>
      </div>
      {/* Only when something was actually damped: on a client with no guards this is
          a paragraph about a thing that did not happen. */}
      {rows.some((r) => r.guards?.length) && (
        <div className="sv-breakdown-note sv-breakdown-guardnote">
          <strong>Severity × Weight = risk points</strong>, then any guard reduces them
          — shown as <span className="sv-breakdown-before">before →</span> after.
          Severity is always what was MEASURED; a guard never changes it, only what it
          costs. Guards ask whether the decline is really this client's: whether their
          market (every client in their territory market center) is down against last year, whether they always slow in this season, and
          whether their own year is up. They only ever reduce a signal, never raise one.
        </div>
      )}
      <div className="sv-breakdown-note">
        Weights are fixed at {totalWeight} points across all {signalCount} signals, whether or
        not a client has the data for each. Dividing by only the signals a client is
        eligible for made the accounts we knew least about score highest.
        {scoreVersion ? ` Score ${scoreVersion}.` : ''}
      </div>
    </div>
  );
}

export default SalesScoreBreakdown;
